import { fetchI18nPackage } from '@/api/reolink/serverApi/common/I18N';
import * as serverApi from '@/api/reolink/serverApi/recommendations';
import { getRecommendationProductInfo } from '@/api/reolink/clientApi/store/product';
import type { ILangAndCountry } from '@/interface/IApi';
import type * as C from './IComponent';
import type { IProduct } from '@reolink-deps/reolink_web.fe.shared-modules';
import type { IStoreProductItem } from '@/api/reolink/serverApi/recommendations';

type IFetchProps = ILangAndCountry & Pick<C.IBaseProps, 'linkText'>;

const fetchCategoryList = async ({ country, lang }: ILangAndCountry): Promise<Omit<C.ICategoryListProps, 'linkText'>> => {

    // 获取分类下的产品列表数据
    const productList = await serverApi.getProductList({ country, lang });

    const categoryList = await serverApi.getCategoryList({ country, lang });
    const categoryKeyMap = new Map(categoryList.map(item => [item.key, item]));

    const categoryProductMap: Record<string, IStoreProductItem[]> = {};

    for (const item of productList) {
        if (item.category.isNormal) {
            const products = categoryProductMap[item.category.key] ?? [];
            products.push(...item.products);
            categoryProductMap[item.category.key] = products;
        }
        else {
            for (const product of item.products) {
                for (const category of product.categories) {
                    if (categoryKeyMap.get(category.key)?.isNormal) {
                        const products = categoryProductMap[category.key] ?? [];
                        products.push(product);
                        categoryProductMap[category.key] = products;
                        break;
                    }
                }
            }
        }
    }

    const list: C.ICategoryListProps['list'] = [];
    const allProductId: Array<string | number> = [];
    
    for (const categoryKey in categoryProductMap) {
        const categoryInfo = categoryKeyMap.get(categoryKey);
        if (!categoryInfo) {
            continue;
        }
        // 先排序产品
        categoryProductMap[categoryKey] = (categoryProductMap[categoryKey] ?? []).sort((a, b) => {
            if (a.sort === b.sort) {
                // 如果sort相等，则按照id排序，id越大越靠前（既产品越新越靠前）
                return b.id - a.id;
            }
            else {
                return a.sort - b.sort;
            }
        });
        list.push({
            id: categoryInfo.id,
            name: categoryInfo.name,
            description: categoryInfo.description,
            image: categoryInfo.image,
            title: categoryInfo.name,
            link: categoryInfo.link,
            note: categoryInfo.note,
            sort: categoryInfo.sort ?? Infinity,
            products: (categoryProductMap[categoryKey] ?? []).slice(0, 4)
        });
        allProductId.push(...categoryProductMap[categoryKey].map(item => item.id));
    }
    // 对分类进行排序
    list.sort((a, b) => {

        if (a.sort === b.sort) {

            // 分类排序一致则按照id排序，id越小越靠前(既分类越旧越靠前)
            return a.id - b.id;
        }

        return a.sort - b.sort;
    });
    return {
        list,
        allProductId,
        country,
        lang
    };
};

export const fetchProductInfo = async (
    key: string,
    productInfoMap: React.RefObject<Map<string, IProduct[]>>
): Promise<IProduct[]> => {
    let productInfo = productInfoMap.current?.get(key);
    if (productInfo) return productInfo;
    productInfo = await getRecommendationProductInfo(key);
    productInfoMap.current?.set(key, productInfo);
    return productInfo;
};

export const fetch = async ({ country, lang, linkText }: IFetchProps): Promise<C.IBaseProps> => {

    const categoryListObj = await fetchCategoryList({ country, lang });
    const { i18nPackage } = await fetchI18nPackage({ lang, country });
    return {
        ...categoryListObj,
        linkText,
        i18nPackage
    };
};
